import React, { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Loader from 'components/Loader/Loader';
import { API_URL } from '../../configs/Api';
import { getHeaders } from '../../middleware/Api';
import { CardHeader, Typography } from '@mui/material';
import CardContent from '@mui/material/CardContent';
import { parseCSV } from 'helpers/parseFile';
const styles = require('./styles.pcss');

type Props = {
  enqueueSnackBarMessage: Function;
  organizationId: string;
};

type CustomField = {
  id: string;
  name: string;
  isHighVolume?: boolean;
};

export default function BulkCustomFieldOptionsUpload(props: Props) {
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchCustomFields = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          API_URL + `custom-fields/${props.organizationId}`,
          { headers: getHeaders() },
        );
        const data = await response.json();
        if (data.type !== 'data' || !response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        setCustomFields(
          data.payload.filter((customField) => customField.type === 'dropdown'),
        );
      } catch (error) {
        props.enqueueSnackBarMessage('Failed to fetch custom fields.', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomFields();
  }, [props.organizationId, props.enqueueSnackBarMessage]);

  const handleFileUpload = async (fieldId: string, files: FileList | null) => {
    if (!files || files.length === 0) {
      props.enqueueSnackBarMessage('No file selected.', 'error');
      return;
    }

    const file = files[0];
    setIsLoading(true);

    try {
      const data = await parseCSV(file)
      if (!data[0]?.name) {
        throw new Error('csv must have a name column.');
      }
      const fixedData = data.map((datum, ind) => {
        if (!datum.name) {
          throw new Error(`Row ${ind + 1} does not have a value for "name"`);
        }
        return {
          name: datum.name,
          status: datum.status,
        };
      });
      if (data.length > 2000) {
        const customField = customFields.find(
          (field) => field.id === fieldId,
        );
        if (!customField?.isHighVolume) {
          throw new Error(
            'Cannot upload over 2000 options for this custom field. Ask to set high volume flag.',
          );
        }
      }
      const res = await (
        await fetch(API_URL + 'job/import-custom-field-options', {
          method: 'post',
          headers: getHeaders(),
          body: JSON.stringify({
            organizationId: props.organizationId,
            customFieldId: fieldId,
            options: fixedData,
          }),
        })
      ).json();
      if (res.type === 'data') {
        props.enqueueSnackBarMessage(
          'Your custom field options are being processed, please check the app in a minute to see if they were successfully uploaded.',
          'success',
        );
      }
    } catch (error) {
      props.enqueueSnackBarMessage(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Paper className={styles.bulkCustomFieldOptionsUploadCard}>
      <CardHeader
        className={styles.header}
        title="Bulk Custom Field Options Upload"
      />
      <CardContent>
        <Typography
          variant="body1"
          className={styles.description}
        >
          Use this tool to upload options for the expense custom fields listed below.
        </Typography>
        {isLoading ? (
          <div className={styles.infoLoader}>
            <Loader visible={true} />
          </div>
        ) : customFields.length === 0 ? (
          <div className={styles.emptyTableMessage}>
            No custom fields available.
          </div>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Custom Field</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customFields.map((field) => (
                <TableRow key={field.id}>
                  <TableCell>{field.name}</TableCell>
                  <TableCell>
                    <input
                      accept="text/csv"
                      type="file"
                      id={`file-upload-${field.id}`}
                      style={{ display: 'none' }}
                      onChange={(e) => handleFileUpload(field.id, e.target.files)}
                    />
                    <label htmlFor={`file-upload-${field.id}`}>
                      <Button
                        variant="contained"
                        color="primary"
                        component="span"
                        size="small"
                      >
                        Upload Options
                      </Button>
                    </label>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Paper>
  );
}
